// フィラメント比較用の共通データソース
// rating: 0 = 未入力、1〜5 = 評価値

import { calculatedMetrics } from './calculatedMetrics';

export interface FilamentCompareData {
  name: string;
  slug: string;
  // 印刷温度（★1〜5：低温=5、高温=1）
  printTempRating: number;
  nozzleTemp: string; // 例: "190-220°C"
  bedTemp: string; // 例: "50-60°C"
  // 反り（★1〜5：反りにくい=5、反りやすい=1）
  warpingRating: number;
  warpingNote?: string;
  warpingNoteEn?: string;
  // 強度（★1〜5）
  strengthRating: number;
  bendStrengthMPa?: number; // 曲げ強度（MPa）
  strengthNote?: string;
  strengthNoteEn?: string;
  flexuralModulusMPa?: number; // 曲げ弾性率（MPa）
  modulusRating: number; // 曲げ弾性率★（1〜5: 高剛性=5）
  zAxisStrengthMPa?: number; // Z方向曲げ強度（MPa）
  zAxisModulusMPa?: number; // Z方向曲げ弾性率（MPa）
  // クリープ耐性（★1〜5：耐性高い=5、低い=1）
  creepResistanceRating: number;
  creepNote?: string;
  creepNoteEn?: string;
  // 耐熱（★1〜5）
  heatResistanceRating: number;
  heatResistanceTemp?: number; // 耐熱温度（°C）
}

/**
 * 曲げ強度の表示フォーマット(括弧付け判定)
 */
export function formatBendStrength(slug: string, value: number): string {
  const metrics = calculatedMetrics[slug];
  const isMeasured = metrics && metrics.default && metrics.default.strength !== null;
  if (isMeasured) {
    return `${value.toFixed(2)} MPa`;
  }
  return `(${value.toFixed(2)} MPa)`;
}

/**
 * 曲げ弾性率の表示フォーマット(括弧付け判定)
 */
export function formatFlexuralModulus(slug: string, value: number): string {
  const metrics = calculatedMetrics[slug];
  const isMeasured = metrics && metrics.default && metrics.default.modulus !== null;
  if (isMeasured) {
    return `${value.toFixed(0)} MPa`;
  }
  return `(${value.toFixed(0)} MPa)`;
}

export const rawFilamentCompareData: FilamentCompareData[] = [
  {
    name: 'PLA',
    slug: 'pla',
    printTempRating: 5,
    nozzleTemp: '190-220°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    warpingNote: '反りが少なく初心者向け',
    warpingNoteEn: 'Low warping; beginner-friendly',
    strengthRating: 4,
    bendStrengthMPa: 71.76,
    flexuralModulusMPa: 2700,
    modulusRating: 3,
    creepResistanceRating: 2,
    creepNote: '長期荷重に弱い',
    creepNoteEn: 'Not ideal for sustained loads',
    heatResistanceRating: 2,
    heatResistanceTemp: 55
  },
  {
    name: 'PLA+',
    slug: 'pla-plus',
    printTempRating: 5,
    nozzleTemp: '200-230°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    strengthRating: 4,
    bendStrengthMPa: 78.41,
    flexuralModulusMPa: 2800,
    modulusRating: 3,
    creepResistanceRating: 3,
    heatResistanceRating: 2,
    heatResistanceTemp: 60
  },
  {
    name: 'PLA-CF',
    slug: 'pla-cf',
    printTempRating: 4,
    nozzleTemp: '200-230°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    strengthRating: 4,
    bendStrengthMPa: 67.02,
    flexuralModulusMPa: 5500,
    modulusRating: 5,
    creepResistanceRating: 3,
    heatResistanceRating: 2,
    heatResistanceTemp: 60
  },
  {
    name: 'PLA-Silk',
    slug: 'pla-silk',
    printTempRating: 5,
    nozzleTemp: '200-220°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    strengthRating: 2,
    bendStrengthMPa: 45,
    flexuralModulusMPa: 1800,
    modulusRating: 2,
    creepResistanceRating: 2,
    heatResistanceRating: 2,
    heatResistanceTemp: 55
  },
  {
    name: 'Matte PLA',
    slug: 'matte-pla',
    printTempRating: 5,
    nozzleTemp: '200-220°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    strengthRating: 3,
    bendStrengthMPa: 48,
    flexuralModulusMPa: 2500,
    modulusRating: 3,
    creepResistanceRating: 2,
    heatResistanceRating: 2,
    heatResistanceTemp: 55
  },
  {
    name: 'PLA-Pro',
    slug: 'pla-pro',
    printTempRating: 4,
    nozzleTemp: '205-225°C',
    bedTemp: '50-60°C',
    warpingRating: 5,
    strengthRating: 4,
    bendStrengthMPa: 68,
    flexuralModulusMPa: 2600,
    modulusRating: 3,
    creepResistanceRating: 3,
    heatResistanceRating: 3,
    heatResistanceTemp: 70
  },
  {
    name: 'HT-PLA',
    slug: 'ht-pla',
    printTempRating: 4,
    nozzleTemp: '210-230°C',
    bedTemp: '50-70°C',
    warpingRating: 4,
    strengthRating: 4,
    bendStrengthMPa: 65,
    flexuralModulusMPa: 2800,
    modulusRating: 3,
    creepResistanceRating: 3,
    heatResistanceRating: 4,
    heatResistanceTemp: 90
  },
  {
    name: 'PETG',
    slug: 'petg',
    printTempRating: 3,
    nozzleTemp: '220-250°C',
    bedTemp: '70-80°C',
    warpingRating: 4,
    warpingNote: '適度な反り',
    warpingNoteEn: 'Moderate warping',
    strengthRating: 4,
    bendStrengthMPa: 63.59,
    flexuralModulusMPa: 1900,
    modulusRating: 2,
    creepResistanceRating: 3,
    heatResistanceRating: 3,
    heatResistanceTemp: 75
  },
  {
    name: 'PETG-CF',
    slug: 'petg-cf',
    printTempRating: 3,
    nozzleTemp: '230-260°C',
    bedTemp: '70-80°C',
    warpingRating: 4,
    strengthRating: 5,
    bendStrengthMPa: 85,
    flexuralModulusMPa: 5000,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 3,
    heatResistanceTemp: 80
  },
  {
    name: 'PETG-GF',
    slug: 'petg-gf',
    printTempRating: 3,
    nozzleTemp: '230-260°C',
    bedTemp: '70-80°C',
    warpingRating: 4,
    strengthRating: 5,
    bendStrengthMPa: 90,
    flexuralModulusMPa: 4500,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 3,
    heatResistanceTemp: 80
  },
  {
    name: 'ABS',
    slug: 'abs',
    printTempRating: 2,
    nozzleTemp: '220-260°C',
    bedTemp: '80-100°C',
    warpingRating: 2,
    warpingNote: '反りやすい',
    warpingNoteEn: 'Prone to warping',
    strengthRating: 4,
    bendStrengthMPa: 60,
    flexuralModulusMPa: 2300,
    modulusRating: 3,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 95
  },
  {
    name: 'ABS-CF',
    slug: 'abs-cf',
    printTempRating: 2,
    nozzleTemp: '240-270°C',
    bedTemp: '90-110°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 95,
    flexuralModulusMPa: 6000,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 100
  },
  {
    name: 'ABS-GF',
    slug: 'abs-gf',
    printTempRating: 2,
    nozzleTemp: '240-270°C',
    bedTemp: '90-110°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 100,
    flexuralModulusMPa: 5500,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 100
  },
  {
    name: 'ASA',
    slug: 'asa',
    printTempRating: 2,
    nozzleTemp: '240-260°C',
    bedTemp: '80-100°C',
    warpingRating: 2,
    warpingNote: '反りやすい',
    warpingNoteEn: 'Prone to warping',
    strengthRating: 4,
    bendStrengthMPa: 71.66,
    flexuralModulusMPa: 2200,
    modulusRating: 3,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 95
  },
  {
    name: 'ASA-CF',
    slug: 'asa-cf',
    printTempRating: 2,
    nozzleTemp: '250-270°C',
    bedTemp: '90-110°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 98,
    flexuralModulusMPa: 5500,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 100
  },
  {
    name: 'ASA-GF',
    slug: 'asa-gf',
    printTempRating: 2,
    nozzleTemp: '250-270°C',
    bedTemp: '90-110°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 105,
    flexuralModulusMPa: 5000,
    modulusRating: 5,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 100
  },
  {
    name: 'PC',
    slug: 'pc',
    printTempRating: 1,
    nozzleTemp: '260-310°C',
    bedTemp: '90-110°C',
    warpingRating: 2,
    warpingNote: '反りやすい',
    warpingNoteEn: 'Prone to warping',
    strengthRating: 5,
    bendStrengthMPa: 110,
    flexuralModulusMPa: 2400,
    modulusRating: 3,
    creepResistanceRating: 5,
    heatResistanceRating: 5,
    heatResistanceTemp: 130
  },
  {
    name: 'PC-CF',
    slug: 'pc-cf',
    printTempRating: 1,
    nozzleTemp: '270-320°C',
    bedTemp: '100-120°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 140,
    flexuralModulusMPa: 7000,
    modulusRating: 5,
    creepResistanceRating: 5,
    heatResistanceRating: 5,
    heatResistanceTemp: 135
  },
  {
    name: 'PC-GF',
    slug: 'pc-gf',
    printTempRating: 1,
    nozzleTemp: '270-320°C',
    bedTemp: '100-120°C',
    warpingRating: 2,
    strengthRating: 5,
    bendStrengthMPa: 150,
    flexuralModulusMPa: 6500,
    modulusRating: 5,
    creepResistanceRating: 5,
    heatResistanceRating: 5,
    heatResistanceTemp: 135
  },
  {
    name: 'PA（ナイロン）',
    slug: 'pa',
    printTempRating: 2,
    nozzleTemp: '240-270°C',
    bedTemp: '70-90°C',
    warpingRating: 3,
    warpingNote: '吸湿性に注意',
    warpingNoteEn: 'Moisture-sensitive',
    strengthRating: 5,
    bendStrengthMPa: 85,
    flexuralModulusMPa: 2800,
    modulusRating: 3,
    creepResistanceRating: 4,
    heatResistanceRating: 4,
    heatResistanceTemp: 100
  },
  {
    name: 'PA-CF',
    slug: 'pa-cf',
    printTempRating: 2,
    nozzleTemp: '250-280°C',
    bedTemp: '80-100°C',
    warpingRating: 3,
    strengthRating: 5,
    bendStrengthMPa: 74.50,
    flexuralModulusMPa: 6000,
    modulusRating: 5,
    creepResistanceRating: 5,
    heatResistanceRating: 4,
    heatResistanceTemp: 105
  },
  {
    name: 'PA-GF',
    slug: 'pa-gf',
    printTempRating: 2,
    nozzleTemp: '250-280°C',
    bedTemp: '80-100°C',
    warpingRating: 3,
    strengthRating: 5,
    bendStrengthMPa: 130,
    flexuralModulusMPa: 5500,
    modulusRating: 5,
    creepResistanceRating: 5,
    heatResistanceRating: 4,
    heatResistanceTemp: 105
  },
  {
    name: 'TPU',
    slug: 'tpu',
    printTempRating: 4,
    nozzleTemp: '210-230°C',
    bedTemp: '40-60°C',
    warpingRating: 5,
    warpingNote: '柔軟性あり',
    warpingNoteEn: 'Flexible',
    strengthRating: 0,
    strengthNote: '柔軟材のため強度評価なし',
    strengthNoteEn: 'Strength rating not applicable for flexible material',
    flexuralModulusMPa: 50,
    modulusRating: 1,
    creepResistanceRating: 3,
    creepNote: '柔軟材のため評価が異なる',
    creepNoteEn: 'Different rating for flexible material',
    heatResistanceRating: 3,
    heatResistanceTemp: 70
  }
];

export const filamentCompareData: FilamentCompareData[] = rawFilamentCompareData.map(data => {
  const metrics = calculatedMetrics[data.slug];
  if (metrics) {
    const measuredModulus = metrics.default.modulus !== null ? metrics.default.modulus : undefined;
    // 実測弾性率から★を再計算
    let modulusRating = data.modulusRating;
    if (measuredModulus !== undefined) {
      if (measuredModulus < 1500) modulusRating = 1;
      else if (measuredModulus < 2000) modulusRating = 2;
      else if (measuredModulus < 3000) modulusRating = 3;
      else if (measuredModulus < 4500) modulusRating = 4;
      else modulusRating = 5;
    }
    return {
      ...data,
      bendStrengthMPa: metrics.default.strength !== null ? metrics.default.strength : data.bendStrengthMPa,
      flexuralModulusMPa: measuredModulus !== undefined ? measuredModulus : data.flexuralModulusMPa,
      modulusRating,
      zAxisStrengthMPa: metrics.zAxis.strength !== null ? metrics.zAxis.strength : undefined,
      zAxisModulusMPa: metrics.zAxis.modulus !== null ? metrics.zAxis.modulus : undefined,
    };
  }
  return data;
});
